import React, { useMemo, useCallback } from "react"
import { Formik, Form, Field, FieldProps, FormikHelpers } from "formik"
import * as Yup from "yup"
import { TextField, Grid } from "@mui/material"
import { useCreateUserMutation, useUpdateUserMutation } from "../../redux/user/user.api"
import { CustomDialog } from "../../styles/styledComponents/CustomDialog"
import PhoneVerificationDialog from "../PhoneVerificationDialog"
import { useCustomSnackbar } from "../multiSnackbar"
import {
  validateName,
  validatePin,
  validatePhone,
  validateEmail,
  validateDriversLicense,
} from "../../constants/AddAccountValidations"
import { AddAccountDialogProps, AddAccountUser, FieldConfig } from "../../types/IAddAccount"
import { formatPhoneNumber } from "../../utils/formattingFunctions/formatPhoneNumber"

/* ─────────────────────────────────────────────────────────── */
/* 1. STATIC FIELD CONFIG – none are inherently required       */
/* ─────────────────────────────────────────────────────────── */

const baseFieldsConfig: readonly FieldConfig[] = [
  {
    name: "phone",
    label: "Phone",
    maxLength: 12,
    type: "tel",
    errorMessage: "Invalid phone",
    required: false,
  },
  {
    name: "password",
    label: "PIN",
    maxLength: 8,
    type: "password",
    errorMessage: "Invalid PIN",
    required: false,
  },
  {
    name: "firstName",
    label: "First Name",
    maxLength: 50,
    type: "text",
    errorMessage: "Invalid first name",
    required: false,
  },
  {
    name: "lastName",
    label: "Last Name",
    maxLength: 50,
    type: "text",
    errorMessage: "Invalid last name",
    required: false,
  },
  {
    name: "email",
    label: "Email",
    maxLength: 255,
    type: "email",
    errorMessage: "Invalid email",
    required: false,
  },
  {
    name: "driversLicense",
    label: "Driver's License",
    maxLength: 20,
    type: "text",
    errorMessage: "Invalid driver's license",
    required: false,
  },
] as const

const getFieldsConfig = (isSimplifiedLogin: boolean) =>
  isSimplifiedLogin ? baseFieldsConfig.filter((f) => f.name !== "password") : baseFieldsConfig

/* ─────────────────────────────────────────────────────────── */
/* 2. YUP SCHEMA – drives phone.required from the new rule     */
/* ─────────────────────────────────────────────────────────── */

const getValidationSchema = (
  isSimplifiedLogin: boolean,
  isPhoneRequired: boolean,
  isEdit = false
) => {
  const baseSchema = {
    firstName: Yup.string().test(
      "validateName",
      "Invalid first name",
      (v?: string) => !v || validateName(v, true) === ""
    ),
    lastName: Yup.string().test(
      "validateName",
      "Invalid last name",
      (v?: string) => !v || validateName(v, true) === ""
    ),
    email: Yup.string().test(
      "validateEmail",
      "Invalid email",
      (v?: string) => !v || validateEmail(v, true) === ""
    ),
    phone: isPhoneRequired
      ? Yup.string()
          .required("Phone is required")
          .test("validatePhone", "Invalid phone", (v?: string) =>
            v ? validatePhone(v) === "" : false
          )
      : Yup.string().test(
          "validatePhone",
          "Invalid phone",
          (v?: string) => !v || validatePhone(v) === ""
        ),
    driversLicense: Yup.string().test(
      "validateDriversLicense",
      "Invalid driver's license",
      (v?: string) => !v || validateDriversLicense(v) === ""
    ),
  }

  if (isSimplifiedLogin) return Yup.object().shape(baseSchema)

  const pinPolicyMsg =
    "PIN Policy: Use complex PINs. Avoid consecutive (e.g., 12345) or repeated numbers (e.g., 111111)."
  const passwordCreate = Yup.string()
    .required("PIN is required")
    .test("validatePin", pinPolicyMsg, (v?: string) => !!v && validatePin(v) === "")

  const passwordEdit = Yup.string().test(
    "validatePin",
    pinPolicyMsg,
    (v?: string) => !v || validatePin(v) === ""
  )

  return Yup.object().shape({ ...baseSchema, password: isEdit ? passwordEdit : passwordCreate })
}

/* ─────────────────────────────────────────────────────────── */
/* 3. MAIN COMPONENT                                           */
/* ─────────────────────────────────────────────────────────── */

export const AddAccountDialog: React.FC<AddAccountDialogProps> = ({
  open,
  onClose,
  onUserUpdatedOrCreated,
  isEdit,
  initialUser,
  fetchUser,
}) => {
  /* Runtime flags */
  const isSimplifiedLogin = localStorage.getItem("simplifiedLogin") === "true"
  const phoneValidationRequired = useMemo(() => {
    const shiftRaw = sessionStorage.getItem("authShift")
    return !!(shiftRaw && JSON.parse(shiftRaw)?.response?.parameters?.phoneValidationRequired)
  }, [])

  /* ── FIX: phone is required whenever we are NOT in simplified mode ── */
  const isPhoneRequired = !isSimplifiedLogin

  /* Form meta */
  const fieldsConfig = useMemo(() => getFieldsConfig(isSimplifiedLogin), [isSimplifiedLogin])
  const validationSchema = useMemo(
    () => getValidationSchema(isSimplifiedLogin, isPhoneRequired, isEdit),
    [isSimplifiedLogin, isPhoneRequired, isEdit]
  )

  /* API & UX */
  const [createUser] = useCreateUserMutation()
  const [updateUser] = useUpdateUserMutation()
  const showSnackbar = useCustomSnackbar()

  /* Local state */
  const [isLoading, setIsLoading] = React.useState(false)
  const [verifyDlgOpen, setVerifyDlgOpen] = React.useState(false)
  const [playerIdForVerification, setPlayerIdForVerification] = React.useState("")
  const [focusedField, setFocusedField] = React.useState<string | null>(null)

  /* Initial values */
  const initialValues: AddAccountUser = initialUser
    ? {
        playerNum: initialUser.playerNum,
        firstName: initialUser.firstName ?? "",
        lastName: initialUser.lastName ?? "",
        password: initialUser.password ?? "",
        phone: initialUser.phone ?? "",
        email: initialUser.email ?? "",
        driversLicense: initialUser.driversLicense ?? "",
      }
    : {
        playerNum: 0,
        firstName: "",
        lastName: "",
        password: "",
        phone: "",
        email: "",
        driversLicense: "",
      }

  /* Submit */
  const handleSubmit = useCallback(
    async (values: AddAccountUser, { resetForm }: FormikHelpers<AddAccountUser>) => {
      setIsLoading(true)

      const payload: Partial<AddAccountUser> = {
        ...(isEdit ? { playerNum: values.playerNum } : {}),
        firstName: values.firstName?.trim() ?? "",
        lastName: values.lastName?.trim() ?? "",
        email: values.email?.trim() ?? "",
        phone: values.phone?.replace(/\D/g, "") ?? "",
        driversLicense: values.driversLicense?.trim() ?? "",
      }

      if (!isSimplifiedLogin && (!isEdit || values.password?.trim())) {
        payload.password = values.password?.trim()
      }

      try {
        // UpdateUser returns an empty 200 body, so on edit the player number
        // comes from the form; only CreateUser returns the PlayerPOCO.
        const res = isEdit ? await updateUser(payload).unwrap() : await createUser(payload).unwrap()
        const playerNumber = isEdit ? values.playerNum : res?.playerNumber

        if (fetchUser && playerNumber) await fetchUser(playerNumber)
        if (!isEdit) localStorage.setItem("selectedUser", JSON.stringify(res))
        resetForm()
        onClose()

        if (!isSimplifiedLogin && phoneValidationRequired && res?.id) {
          setPlayerIdForVerification(res.id)
          setVerifyDlgOpen(true)
        }

        onUserUpdatedOrCreated()
        showSnackbar(
          isEdit ? "User updated successfully!" : "User created successfully!",
          "success"
        )
      } catch (err: any) {
        showSnackbar(err?.data || "Operation failed.", "error")
      } finally {
        setIsLoading(false)
      }
    },
    [
      isEdit,
      isSimplifiedLogin,
      phoneValidationRequired,
      createUser,
      updateUser,
      fetchUser,
      onClose,
      onUserUpdatedOrCreated,
      showSnackbar,
    ]
  )

  /* ─────────────────────────────────────────────────────────── */

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          dirty,
          submitForm,
          setFieldValue,
          values,
          resetForm,
          isSubmitting,
          submitCount,
        }) => {
          const handleDialogClose = () => {
            resetForm()
            onClose()
          }

          return (
            <CustomDialog
              open={open}
              title={isEdit ? "Edit account" : "Create new account"}
              content={
                <Form autoComplete="off">
                  <Grid container spacing={2} mt={1}>
                    {fieldsConfig.map((field) => (
                      <Grid item xs={12} key={field.name}>
                        <Field name={field.name}>
                          {({ field: formikField }: FieldProps) => {
                            const showError =
                              !!errors[field.name] &&
                              ((touched[field.name] &&
                                values[field.name as keyof AddAccountUser] !==
                                  initialValues[field.name as keyof AddAccountUser] &&
                                focusedField !== field.name) ||
                                submitCount > 0)

                            return (
                              <TextField
                                fullWidth
                                label={field.label}
                                type={field.type}
                                placeholder={isEdit && field.name === "password" ? "********" : ""}
                                InputLabelProps={{ shrink: true }}
                                error={showError}
                                helperText={showError ? (errors[field.name] as string) : ""}
                                inputProps={{
                                  maxLength: field.maxLength,
                                  className: field.name === "password" ? "key" : "",
                                  pattern: field.name === "phone" ? "\\d*" : undefined,
                                }}
                                autoComplete={field.name === "password" ? "new-password" : "off"}
                                inputMode={
                                  field.name === "phone" || field.name === "password"
                                    ? "numeric"
                                    : undefined
                                }
                                required={
                                  field.name === "password"
                                    ? !isSimplifiedLogin
                                    : field.name === "phone"
                                    ? isPhoneRequired
                                    : false
                                }
                                {...formikField}
                                onFocus={() => setFocusedField(field.name)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  if (field.name === "phone") {
                                    const digits = e.target.value.replace(/\D+/g, "")
                                    setFieldValue("phone", formatPhoneNumber(digits))
                                  } else {
                                    formikField.onChange(e)
                                  }
                                }}
                                onBlur={(e) => {
                                  formikField.onBlur(e)
                                  setFocusedField(null)
                                }}
                              />
                            )
                          }}
                        </Field>
                      </Grid>
                    ))}
                  </Grid>
                </Form>
              }
              primaryActionText={isEdit ? "Update" : "Create"}
              secondaryActionText="Close"
              onPrimaryAction={submitForm}
              isLoading={isLoading}
              disabledPrimaryAction={
                isSubmitting ||
                isLoading ||
                (isEdit
                  ? !dirty || Object.keys(errors).length > 0
                  : isSimplifiedLogin
                  ? Object.keys(errors).length > 0
                  : Object.keys(errors).length > 0 ||
                    !values.password?.trim() ||
                    (isPhoneRequired && !values.phone.trim()))
              }
              onSecondaryAction={handleDialogClose}
              handleClose={handleDialogClose}
            />
          )
        }}
      </Formik>

      <PhoneVerificationDialog
        open={verifyDlgOpen}
        playerId={playerIdForVerification}
        onClose={() => setVerifyDlgOpen(false)}
      />
    </>
  )
}
